/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: QueueTiersSection.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Call Center module.
----------------------------------------------------------------*/

import { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, Users, GripVertical } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { FormBox } from '../../components/ui/FormBox';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { AgentSelect } from '../../components/ui/AgentSelect';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { TierRow } from './types';

interface QueueTiersSectionProps {
  tiers: TierRow[];
  onChange: (tiers: TierRow[]) => void;
}

export function QueueTiersSection({ tiers, onChange }: QueueTiersSectionProps) {
  const { t } = useI18n();
  const [tierRows, setTierRows] = useState<TierRow[]>(tiers);
  const onChangeRef = useRef(onChange);
  const nextTempIdRef = useRef(0);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    setTierRows(tiers.map(tier => {
      // Ensure each tier has a tempId for sorting if it doesn't have an id
      if (!tier.id && !tier.__tempId) {
        return { ...tier, __tempId: nextTempIdRef.current++ };
      }
      return tier;
    }));
  }, [tiers]);

  const handleTierChange = (index: number, field: keyof TierRow, value: string) => {
    const updated = [...tierRows];
    updated[index] = { ...updated[index], [field]: value };
    setTierRows(updated);
    onChangeRef.current(updated);
  };

  const handleAddTier = () => {
    const newTier: TierRow & { __tempId: number } = {
      agentId: '',
      level: '1',
      position: '1',
      __tempId: nextTempIdRef.current++
    };
    const updated = [...tierRows, newTier];
    setTierRows(updated);
    onChangeRef.current(updated);
  };

  const handleRemoveTier = (index: number) => {
    const updated = tierRows.filter((_, i) => i !== index);
    setTierRows(updated);
    onChangeRef.current(updated);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setTierRows((items) => {
        const oldIndex = items.findIndex((tier, i) => {
          const id = tier.id ?? tier.__tempId ?? i;
          return id === active.id || id === Number(active.id);
        });
        const newIndex = items.findIndex((tier, i) => {
          const id = tier.id ?? tier.__tempId ?? i;
          return id === over.id || id === Number(over.id);
        });

        if (oldIndex === -1 || newIndex === -1) {
          return items;
        }

        const updated = arrayMove(items, oldIndex, newIndex);
        onChangeRef.current(updated);
        return updated;
      });
    }
  };

  return (
    <FormBox title={t('queues.form.sections.tiers')} icon={<Users className="h-5 w-5" />}>
      <div className="space-y-4">
        {tierRows.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {t('queues.form.noTiers')}
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={tierRows.map((tier, i) => tier.id ?? tier.__tempId ?? i)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                <div className="hidden md:grid grid-cols-12 gap-3 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <span className="col-span-3 flex items-center gap-2">
                    <GripVertical className="h-3 w-3 opacity-0" />
                    {t('queues.form.fields.agent.label')}
                  </span>
                  <span className="col-span-3">
                    {t('queues.form.fields.level.label')}
                  </span>
                  <span className="col-span-3">
                    {t('queues.form.fields.position.label')}
                  </span>
                  <span className="col-span-3 text-right">
                    {t('common.actions')}
                  </span>
                </div>
                {tierRows.map((tier, index) => (
                  <TierRowItem
                    key={tier.id ?? tier.__tempId ?? index}
                    tier={tier}
                    index={index}
                    onUpdate={handleTierChange}
                    onRemove={handleRemoveTier}
                    t={t}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}

        <div className="flex justify-end">
          <Button type="button" onClick={handleAddTier} variant="outline" size="sm">
            <Plus className="h-4 w-4 mr-2" />
            {t('common.add')}
          </Button>
        </div>
      </div>
    </FormBox>
  );
}

interface TierRowItemProps {
  tier: TierRow;
  index: number;
  onUpdate: (index: number, field: keyof TierRow, value: string) => void;
  onRemove: (index: number) => void;
  t: (key: string) => string;
}

function TierRowItem({ tier, index, onUpdate, onRemove, t }: TierRowItemProps) {
  const sortableId = tier.id ?? tier.__tempId ?? index;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: sortableId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`grid grid-cols-12 gap-3 p-3 border border-border rounded-md bg-card ${isDragging ? 'shadow-lg ring-2 ring-primary/40' : ''}`}
    >
      <div className="col-span-12 md:col-span-3 flex items-center gap-2">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted/60 rounded"
          aria-label={t('queues.form.actions.reorder')}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </button>
        <div className="flex-1">
          <span className="md:hidden text-xs font-medium text-muted-foreground">
            {t('queues.form.fields.agent.label')}
          </span>
          <AgentSelect
            value={tier.agentId}
            onChange={(value) => onUpdate(index, 'agentId', value)}
            placeholder={t('queues.form.fields.agent.select')}
          />
        </div>
      </div>

      <div className="col-span-12 md:col-span-3">
        <span className="md:hidden text-xs font-medium text-muted-foreground">
          {t('queues.form.fields.level.label')}
        </span>
        <Input
          id={`level-${index}`}
          type="number"
          min="1"
          value={tier.level}
          onChange={(e) => onUpdate(index, 'level', e.target.value)}
        />
      </div>

      <div className="col-span-12 md:col-span-3">
        <span className="md:hidden text-xs font-medium text-muted-foreground">
          {t('queues.form.fields.position.label')}
        </span>
        <Input
          id={`position-${index}`}
          type="number"
          min="1"
          value={tier.position}
          onChange={(e) => onUpdate(index, 'position', e.target.value)}
        />
      </div>

      <div className="col-span-12 md:col-span-3 flex items-center md:justify-end">
        <span className="md:hidden text-xs font-medium text-muted-foreground">
          {t('common.actions')}
        </span>
        <SimpleTooltip content={t('common.remove') || 'Remove'}>
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="ml-auto md:ml-0 p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
          >
            <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
          </button>
        </SimpleTooltip>
      </div>
    </div>
  );
}

